import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import type { Collection } from 'mongodb';
import { Change, nothingHere } from '@adornis/base/change.js';
import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity } from '@adornis/baseql/decorators.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { context, serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import {
  getCurrentUserServer,
  getCurrentUserServerOrFail,
  loadRawEntityDataByID,
  loadRawEntityDataByIDOrFail,
  loadRawEntityDataByIDs,
  loadRawEntityDataByQuery,
} from '@adornis/bh-util/util.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import {
  ConvertConfig,
  FieldMappingEntry,
  MaybeNumberFieldMappingEntry,
  NumberFieldMappingEntry,
} from '@adornis/import-export/db/convert-config.js';
import { ImportJob } from '@adornis/import-export/db/import-job.js';
import { exportCSVFromEntities } from '@adornis/import-export/server/export-internals.js';
import { convertCSVToEntity } from '@adornis/import-export/server/import-internals.js';
import { Language } from '@adornis/translation-core/languages.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { format } from 'date-fns';
import { readFile, stat, unlink, writeFile } from 'fs/promises';
import path from 'path';
import PizZip from 'pizzip';
import { throttleTime, map, switchMap, of, Observable } from 'rxjs';
import { getDbPluginForScope } from '../db-plugins.js';
import { BHInvoice } from '../entities/BHInvoice.js';
import { BHPayment } from '../entities/BHPayment.js';
import { BHFilter } from '../entities/bh-filter.js';
import { Category } from '../entities/category.js';
import { Institution } from '../entities/institution.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationExportCSV } from '../entities/ParticipationExportCSV.js';
import type { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { getMergedProductSettings, Setting } from '../entities/settings.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { Teaching } from '../entities/teaching.js';
import { User } from '../entities/user.js';
import { WorkshopImportFile } from '../entities/workshop-import-file.js';
import {
  DescriptionFieldMapping,
  LenientBooleanFieldMappingEntry,
  MaybeDateTimeFieldMappingEntry,
  MaybeStringFieldMappingEntry,
  ParticipationPaymentDataExportCSV,
  PriceFieldMapping,
  Workshop,
} from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission, Role } from '../enums/roles.js';
import { ExportError } from '../errors/ExportError.js';
import { notifyLecturerEvaluationMail } from '../notification/lecturer-evaluation-mail.js';
import { notifyLecturerWorkshopReminder } from '../notification/lecturer-workshop-reminder.js';
import { generateEvaluationQrCodePdf, generateTnQrCodePdf } from '../notification/qr-pdf-generation.js';
import { generateSigningListInWorker as generateSigningListPdf } from '../server/generate-signing-list-worker.js';
import { mongo } from '../server/mongo.js';
import { attachImportRowSkipping, importSummaryJSON, type ImportSummary } from '../server/import-row-skipping.js';
import { formatBerlinDate, lookupEntityById, parseBerlinDateTime, toBerlinDate, withIdTiebreaker } from '../util.js';
import { isWhatsappConfiguredForInstitution } from '../whatsapp/util.js';
import { getWorkshopAppointmentParticipationConfirmationLink } from './participation.js';
import { getExistingStammdaten } from './stammdaten.js';
import { getAdornisFileByID } from './files.js';

export async function calculateWorkshopLeftCapacity(workshop: NarrowedEntityData<Workshop, { capacity: 1; _id: 1 }>) {
  if (workshop._id === undefined) throw new Error('workshop id not defined');
  if (workshop.capacity === undefined) throw new Error('capacity not defined');
  const c = mongo().collection(Participation._collectionName); // ignore publish filter
  const participationCount = await c.countDocuments({
    productID: workshop._id,
    state: { $in: [ParticipationState.in, ParticipationState.participated] },
  });

  return Math.max((workshop.capacity || 0) - participationCount, 0);
}

export const getWorkshopParticipants = registerQuery({
  operationName: 'getWorkshopParticipants',
  type: () => [Participation],
  params: [
    { name: 'workshopID', type: () => String },
    { name: 'state', type: () => [String] },
  ],
  resolve: (workshopID: string, state: ParticipationState[]) => {
    return async (gqlFields: BaseQLSelectionSet<Participation>) => {
      const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, { institutionID: 1 });
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        if (!currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, workshop.institutionID))
          throw new ExpectedError('not authorized');
      }
      const participations = await loadRawEntityDataByQuery(Participation, {
        productID: workshopID,
        state: { $in: state },
      });
      return participations;
    };
  },
});

export const generateParticipationSigningListPdf = registerMutation({
  type: () => String,
  operationName: 'generateParticipationSigningListPdf',
  params: [{ name: 'workshopID', type: () => String }],
  resolve: (workshopID: string) => {
    return async () => {
      const user = await getCurrentUserServerOrFail();

      const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, {
        name: 1,
        institutionID: 1,
        appointments: { start: 1, end: 1, location: 1 },
      });

      // lecturers teaching this workshop — also used for authorization below
      const teachings = await loadRawEntityDataByQuery(Teaching, { productID: workshopID }, { lecturerID: 1 });

      // Admins may generate the list for any workshop in their institution; a lecturer may generate it
      // for the workshops they actually teach (the profile "Veranstaltungen als Dozierender" view).
      // Without the lecturer branch, lecturer-only accounts hit "no permission" because
      // VIEW_ADMIN_DASHBOARD is an admin permission lecturers don't hold.
      const isAdmin = user.hasPermission(Permission.VIEW_ADMIN_DASHBOARD, workshop.institutionID);
      const isLecturerForWorkshop = teachings.some(t => t.lecturerID === user._id);
      if (!isAdmin && !isLecturerForWorkshop) throw new Error('no permission');

      // load participants (batched, no N+1)
      const participations = await loadRawEntityDataByQuery(
        Participation,
        { productID: workshopID, state: { $in: [ParticipationState.in] } },
        { userID: 1 },
      );
      const userIDs = participations.map(p => p.userID).filter(Boolean) as string[];
      const users =
        userIDs.length > 0 ? await loadRawEntityDataByIDs(User, userIDs, { _id: 1, firstName: 1, lastName: 1 }) : [];

      // load lecturers (batched, no N+1)
      const lecturerIDs = teachings.map(t => t.lecturerID).filter(Boolean) as string[];
      const lecturers =
        lecturerIDs.length > 0 ? await loadRawEntityDataByIDs(User, lecturerIDs, { firstName: 1, lastName: 1 }) : [];

      // load institution logo
      let institutionLogoBuffer: Buffer | null = null;
      const institution = await loadRawEntityDataByID(Institution, workshop.institutionID, { logoID: 1 });
      if (institution?.logoID) {
        try {
          const logoFile = await getAdornisFileByID(institution.logoID)(selectionSet(() => AdornisFile, 2));
          if (logoFile) institutionLogoBuffer = await readFile(logoFile.getFullPath());
        } catch {
          /* logo loading failed, continue without */
        }
      }

      // load global logo
      let globalLogoBuffer: Buffer | null = null;
      const globalSetting = await mongo().collection('global-setting').findOne({});
      if (globalSetting?.logoID) {
        try {
          const logoFile = await getAdornisFileByID(globalSetting.logoID)(selectionSet(() => AdornisFile, 2));
          if (logoFile) globalLogoBuffer = await readFile(logoFile.getFullPath());
        } catch {
          /* logo loading failed, continue without */
        }
      }

      const buffer = await generateSigningListPdf({
        workshopName: workshop.name ?? '',
        appointments: workshop.appointments ?? [],
        participants: users.map(u => ({ firstName: u.firstName ?? '', lastName: u.lastName ?? '' })),
        lecturers: lecturers.filter(Boolean).map(l => ({ firstName: l.firstName ?? '', lastName: l.lastName ?? '' })),
        institutionLogoBuffer,
        globalLogoBuffer,
      });

      const name = `${workshop.name} - Teilnehmerzettel.pdf`;
      const file = await AdornisFile.createFromBuffer(buffer, name);

      return A.absoluteUrl(file.getServeLink() + '/' + name);
    };
  },
});

export const generateEvaluationQrCodePdfForWorkshop = registerMutation({
  type: () => String,
  operationName: 'generateEvaluationQrCodePdfForWorkshop',
  params: [{ name: 'workshopID', type: () => String }],
  resolve: (workshopID: string) => {
    return async () => {
      const user = await getCurrentUserServer();
      if (!user) throw new Error('user not found');

      const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, {
        name: 1,
        institutionID: 1,
        appointments: { _id: 1, start: 1, end: 1, location: 1, name: 1 },
      });

      const productSettings = await getMergedProductSettings(workshopID, [
        'courseLocation',
        'evaluationLink',
        'evaluationLinkCode',
      ]);

      const evaluationLink = productSettings.evaluationLink;
      if (!evaluationLink) throw new ExpectedError('No evaluation link configured for this workshop');

      let logoBuffer: Buffer | null = null;
      const institution = await loadRawEntityDataByID(Institution, workshop.institutionID, { logoID: 1 });
      if (institution?.logoID) {
        try {
          const logoFile = await getAdornisFileByID(institution.logoID)(selectionSet(() => AdornisFile, 2));
          if (logoFile) {
            logoBuffer = await readFile(logoFile.getFullPath());
          }
        } catch {
          // Logo loading failed, continue without logo
        }
      }

      const buffer = await generateEvaluationQrCodePdf({
        workshopName: workshop.name ?? '',
        courseLocation: productSettings.courseLocation ?? '',
        appointments: workshop.appointments ?? [],
        evaluationLink,
        evaluationCode: productSettings.evaluationLinkCode ?? '',
        logoBuffer,
      });

      const name = `${workshop.name} - Evaluation QR-Code.pdf`;
      const file = await AdornisFile.createFromBuffer(buffer, name);
      return A.absoluteUrl(file.getServeLink() + '/' + name);
    };
  },
});

export const generateTnQrCodePdfForWorkshop = registerMutation({
  type: () => String,
  operationName: 'generateTnQrCodePdfForWorkshop',
  params: [{ name: 'workshopID', type: () => String }],
  resolve: (workshopID: string) => {
    return async () => {
      const user = await getCurrentUserServer();
      if (!user) throw new Error('user not found');

      const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, {
        name: 1,
        institutionID: 1,
        appointments: { _id: 1, start: 1, end: 1, location: 1, name: 1 },
      });

      const productSettings = await getMergedProductSettings(workshopID, ['courseLocation']);

      let logoBuffer: Buffer | null = null;
      const institution = await loadRawEntityDataByID(Institution, workshop.institutionID, { logoID: 1 });
      if (institution?.logoID) {
        try {
          const logoFile = await getAdornisFileByID(institution.logoID)(selectionSet(() => AdornisFile, 2));
          if (logoFile) {
            logoBuffer = await readFile(logoFile.getFullPath());
          }
        } catch {
          // Logo loading failed, continue without logo
        }
      }

      const allConfirmationLinks = await getWorkshopAppointmentParticipationConfirmationLink(workshopID)({
        appointmentID: 1,
        url: 1,
        validFrom: 1,
        validTill: 1,
      });

      if (allConfirmationLinks.length === 0) throw new Error('No confirmation links available for this workshop');

      const tnPdf = await generateTnQrCodePdf({
        workshopName: workshop.name ?? '',
        courseLocation: productSettings?.courseLocation ?? '',
        appointments: workshop.appointments ?? [],
        confirmationLinks: allConfirmationLinks,
        logoBuffer,
      });

      const name = `${workshop.name} - Teilnahmeaushang.pdf`;
      const file = await AdornisFile.createFromBuffer(tnPdf, name);
      return A.absoluteUrl(file.getServeLink() + '/' + name);
    };
  },
});

const APPOINTMENTS_START_FIELD = 'appointments.start';

/**
 * The workshop list's "Zukünftige / Vergangene" tabs encode their temporal split as a `DateTimeAspect`
 * on the `appointments.start` array. That aspect is unreliable as a query source for two reasons:
 *   1. Its cutoff is captured on the client as `new Date()` and persisted with the saved filter, so it
 *      goes stale — already-finished workshops keep satisfying `start >= cutoff` and leak into "Zukünftige".
 *   2. A plain `$gte`/`$lte` on an array matches if ANY element qualifies, so a workshop with both a past
 *      and a future session shows up in BOTH tabs — i.e. "Vergangene" contains events that aren't past.
 *
 * So we ignore the client aspect's value and compile the split here, at query time, against the server
 * clock, as two mutually-exclusive sets that mirror `participationHasFutureAppointment`:
 *   - upcoming ('after'): the workshop has at least one session starting in the future
 *   - past ('before'):    the workshop has NO session starting in the future (exact negation → no overlap)
 *
 * Returns the temporal `$match` clause, or null when no temporal tab is active (e.g. the "Alle" tab).
 */
function buildAppointmentTemporalMatch(filter: BHFilter): Record<string, any> | null {
  const aspect = filter.aspects?.find(a => a.fieldName === APPOINTMENTS_START_FIELD) as any;
  if (!aspect || (aspect.mode !== 'after' && aspect.mode !== 'before')) return null;
  const hasFutureSession = { appointments: { $elemMatch: { start: { $gt: new Date() } } } };
  // `$nor` is the clean top-level negation: "does not have a session starting in the future".
  return aspect.mode === 'after' ? hasFutureSession : { $nor: [hasFutureSession] };
}

export const subscribeWorkshopsPaginated = registerSubscription({
  type: () => [Workshop],
  operationName: 'subscribeWorkshopsPaginated',
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip, limit, filter) => (gqlFields: BaseQLSelectionSet<Workshop>) => {
    return watchEntireCollection('products-materialized').pipe(
      throttleTime(1000, undefined, { leading: true, trailing: true }),
      switchMap(() => Promise.resolve(mongo().collection('products-materialized'))),
      switchMap(workshopCollection => {
        if (gqlFields.workshopSetName) {
          gqlFields.affiliation = { _class: 1, affiliation: 1, affiliationID: 1 };
        }

        const temporalMatch = buildAppointmentTemporalMatch(filter);

        // Compile the rest of the filter without the temporal aspect — `temporalMatch` supersedes it.
        // Mutate-and-restore so re-emissions (the subscription re-fires on every collection change)
        // still see the full aspect list.
        const allAspects = filter.aspects ?? [];
        filter.aspects = allAspects.filter(a => a.fieldName !== APPOINTMENTS_START_FIELD);
        const baseMatch = filter.getMongoFilter();
        filter.aspects = allAspects;

        const aggregation = [
          {
            $match: {
              _class: Workshop._class,
              ...(filter.institutionID ? { institutionID: filter.institutionID } : {}),
              ...(filter.timeframeID ? { timeframeID: filter.timeframeID } : {}),
            },
          },
          { $lookup: lookupEntityById(Timeframe, 'timeframeID', 'timeframe') },
          { $set: { timeframe: { $arrayElemAt: ['$timeframe', 0] } } },
          { $lookup: lookupEntityById(Category, 'categoryID', 'category') },
          { $set: { category: { $arrayElemAt: ['$category', 0] } } },
          { $match: baseMatch },
          ...(temporalMatch ? [{ $match: temporalMatch }] : []),
          { $sort: withIdTiebreaker(filter.getMongoOptions().sort) },
          { $project: gqlFields },
          { $skip: skip },
          { $limit: limit },
        ];

        return workshopCollection.aggregate(aggregation, { collation: { locale: 'de' } }).toArray() as Promise<
          Workshop[]
        >;
      }),
    );
  },
});

export const ikh_exportWorkshopParticipantsInvoices = registerQuery({
  operationName: 'ikh_exportWorkshopParticipantsInvoices',
  type: () => String,
  params: [{ name: 'productID', type: () => String }],
  resolve: productID => {
    return async () => {
      const product = await getWorkshopByID(productID)({ _id: 1, institutionID: 1 });
      if (!product) throw new Error('product not found ' + productID);
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        if (!currentUser.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_EXPORTS, product.institutionID))
          throw new ExpectedError('not authorized');
      }

      const invoiceCollection = mongo().collection(BHInvoice._collectionName);
      const invoices = await invoiceCollection.raw.find<BHInvoice>({ institutionID: product.institutionID }).toArray();

      const zip = new PizZip();
      let fileCount = 0;

      for (const invoice of invoices) {
        const payment = await loadRawEntityDataByID(BHPayment, invoice.paymentID, { participationIDs: 1 });
        if (!payment) continue;

        const participations = await loadRawEntityDataByIDs(Participation, payment.participationIDs, { productID: 1 });
        const relevantForProduct = participations.filter(p => p.productID === productID).length >= 1;
        if (!relevantForProduct) continue;

        const stammdaten = await runInContext(serverContext.sessionID, () =>
          getExistingStammdaten(invoice.userID, product.institutionID, null)({ _id: 1, invoiceReceiver: 1 } as any),
        );
        // TODO: this should use an enum and be in a plugin package
        if ((stammdaten as any).invoiceReceiver !== 'IKHEXTERNAL') continue; // we just want external ones

        const invoiceFile = await getAdornisFileByID(invoice.pdfID)(selectionSet(() => AdornisFile, 2));
        if (!invoiceFile) throw new Error('invoice pdf not found: ' + invoice.pdfID);
        const invoiceFilePath = invoiceFile.getFullPath();
        const dataBuffer = await readFile(invoiceFilePath);
        zip.file(`invoice-${invoice._id}.pdf`, dataBuffer);
        fileCount++;
      }

      if (fileCount === 0) throw new Error('file is empty');

      const data = zip.generate({
        type: 'nodebuffer',
        // compression: DEFLATE adds a compression step.
        // For a 50MB output document, expect 500ms additional CPU time
        compression: 'DEFLATE',
      });

      const importExportConfig = await import('@adornis/import-export/config.js').then(
        module => module.importExportConfig,
      );
      const tmpFilePath = path.resolve(path.join(importExportConfig.get('cache'), `invoices-extern-${productID}.zip`));
      await writeFile(tmpFilePath, data);

      const fileName = tmpFilePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  },
});

@Entity()
export class AppointmentFieldMapping extends FieldMappingEntry {
  static override _class = 'AppointmentFieldMapping';

  override serialize = appointments => {
    if (!appointments || appointments.length === 0) return '-';
    return appointments
      .map(ap => {
        const dated = ap.start && ap.end;
        const start = dated ? format(toBerlinDate(ap.start), 'dd.MM.yyyy HH:mm') : '';
        const end = dated ? format(toBerlinDate(ap.end), 'dd.MM.yyyy HH:mm') : '';
        return [ap._id, ap.name?.replace(/:/g, '') ?? '', ap.location ?? '', `${start}-${end}`].join(':');
      })
      .join('//');
  };

  override deserialize = v => {
    const cell = v?.trim() ?? '';
    if (!cell || cell === '-') return [];
    const date = String.raw`\d{1,2}\.\d{1,2}\.(?:\d{4}|\d{2})(?: \d{1,2}:\d{2})?`;
    const appointment = new RegExp(`(?:tbd|([^:/]*):([^:]*):(.*?):(${date})?-(${date})?)(?://|$)`, 'gsy');
    const appointments: Array<Record<string, any>> = [];
    let position = 0;
    while (position < cell.length) {
      if (appointments.length === 200) throw new Error('More than 200 appointments in one Termine cell');
      const match = appointment.exec(cell);
      if (!match) throw new Error(`Invalid appointment: ${cell.slice(position)}`);
      const start = parseBerlinDateTime(match[4]);
      const end = parseBerlinDateTime(match[5]);
      if (start && end && end < start) throw new Error(`Appointment end precedes start: ${match[0]}`);
      appointments.push({
        _class: 'Appointment',
        _id: match[1]?.trim() ? match[1] : A.getGloballyUniqueID(),
        name: match[2] ?? '',
        location: (match[3] ?? '').replace(/^:[\s:]*/, ''),
        start,
        end,
      });
      position = appointment.lastIndex;
      if (position === cell.length && match[0].endsWith('//')) throw new Error(`Invalid appointment: ${cell}`);
    }
    return appointments;
  };
}

const requiredImportNumber = (input: string, output: string) => new FieldMappingEntry({ input, output }, value => {
  if (!value?.trim()) throw new Error('A number is required');
  const number = Number(value);
  if (!Number.isFinite(number)) throw new Error(`Invalid number: ${value}`);
  return number;
});

export const importWorkshopsForInstitution = registerSubscription({
  operationName: 'importWorkshopsForInstitution',
  type: () => String,
  params: [
    { name: 'fileID', type: () => ID },
    { name: 'institutionID', type: () => ID },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_WORKSHOPS, params.institutionID),
    ),
  ],
  resolve: (fileID, institutionID) => {
    return () => new Observable<string>(subscriber => {
      const summary: ImportSummary = { imported: 0, skipped: [] };
      let csvString: string;
      const runJob = (config: ConvertConfig<any>, job: ImportJob<any>) => new Promise<void>((resolve, reject) => {
        const subscription = job.logger.subscribe({
          next: () => subscriber.next('...importing'),
          error: reject,
          complete: resolve,
        });
        try {
          convertCSVToEntity(csvString, config, job);
        } catch (error) {
          subscription.unsubscribe();
          reject(error);
        }
      });
      void (async () => {
        type ImportFileDocument = EntityData<WorkshopImportFile> & { ownerID?: string };
        let authorizedFile: ImportFileDocument | null = null;
        const unlinkImportFile = async (file: ImportFileDocument) => {
          await unlink(WorkshopImportFile.getFullPath(file as WorkshopImportFile)).catch(error => {
            if (error.code !== 'ENOENT') throw error;
          });
        };
        try {
          subscriber.next('...importing');
          const collection = mongo().collection<ImportFileDocument>(AdornisFile._collectionName);
          const staleFilter = {
            _class: WorkshopImportFile._class,
            createdAt: { $lt: new Date(Date.now() - 60 * 60 * 1000) },
          };
          const stale = await collection.find(staleFilter).toArray();
          if (stale.length) {
            await collection.deleteMany({ ...staleFilter, _id: { $in: stale.map(file => file._id!) } });
            await Promise.all(stale.map(unlinkImportFile));
          }
          const doc = await collection.findOne({ _id: fileID, _class: WorkshopImportFile._class });
          if (!doc) throw new Error('Workshop import file not found');
          if (doc.institutionID !== institutionID) throw new Error('Workshop import file belongs to a different institution');
          if (!context.serverContext) {
            const user = await getCurrentUserServer();
            if (!user || doc.ownerID !== user._id) throw new Error('Workshop import file belongs to a different user');
          }
          authorizedFile = doc;
          const bytes = await readFile(WorkshopImportFile.getFullPath(doc as WorkshopImportFile));
          if (bytes.length > 20 * 1024 * 1024) throw new Error('Workshop import file exceeds 20 MiB');
          const content = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? bytes.subarray(3) : bytes;
          try {
            csvString = new TextDecoder('utf-8', { fatal: true }).decode(content);
          } catch {
            csvString = new TextDecoder('windows-1252').decode(content);
          }
          const convertConfig = new ConvertConfig<Workshop>({
            uniqueIdentifierFieldName: '_id',
            className: Workshop._class,
            delimiter: ';',
            upsertFieldBlacklist: ['categoryName', `translatedDescriptionHTML_${Language.ENGLISH}`],
            fieldMappings: [
              { input: 'Kategorie ID', output: 'categoryID' },
              { input: 'Kategorie', output: 'categoryName' },
              new FieldMappingEntry({ input: 'ID', output: '_id' }, v => v?.trim() ?? ''),
              new FieldMappingEntry({ input: 'institutionID', output: 'institutionID' }, () => institutionID),
              { input: 'Semester ID', output: 'timeframeID' },
              new MaybeStringFieldMappingEntry({ input: 'Name', output: 'name' }),
              new MaybeStringFieldMappingEntry({ input: 'Referent', output: 'speaker' }),
              requiredImportNumber('Max. Teilnehmer', 'capacity'),
              new MaybeStringFieldMappingEntry({ input: 'sellerID', output: 'sellerID' }),
              new DescriptionFieldMapping({
                input: 'descriptionGermanHTML', output: 'translatedDescriptionHTML', language: Language.GERMAN,
              }),
              new DescriptionFieldMapping({
                input: 'descriptionEnglishHTML', output: `translatedDescriptionHTML_${Language.ENGLISH}`, language: Language.ENGLISH,
              }),
              new MaybeNumberFieldMappingEntry({ input: 'sortingWeight', output: 'sortingWeight' }),
              new LenientBooleanFieldMappingEntry({ input: 'hasPrice', output: 'hasPrice' }),
              new MaybeStringFieldMappingEntry({ input: 'certificateID', output: 'certificateID' }),
              new MaybeDateTimeFieldMappingEntry({ input: 'lastReminderMailSent', output: 'lastReminderMailSent' }),
              new MaybeStringFieldMappingEntry({ input: 'infoLink', output: 'infoLink' }),
              new LenientBooleanFieldMappingEntry({ input: 'hideCapacity', output: 'hideCapacity' }),
              new MaybeStringFieldMappingEntry({ input: 'reference', output: 'reference' }),
              new MaybeStringFieldMappingEntry({ input: 'terminCopyID', output: 'terminCopyID' }),
              new AppointmentFieldMapping({ input: 'Termine', output: 'appointments' }),
              { input: 'Preis Class', output: 'prices.0._class' },
              new FieldMappingEntry({ input: 'Preis ID', output: 'prices.0._id' }, v => v?.trim() || A.getGloballyUniqueID()),
              new MaybeStringFieldMappingEntry({ input: 'Stripe ID', output: 'prices.0.stripeID' }),
              { input: 'Währung', output: 'prices.0.currency' },
              requiredImportNumber('Betrag', 'prices.0.unitAmount'),
            ],
            foreignKeys: [],
          });
          const workshopJob = new ImportJob<Workshop>({ convertConfig });
          const workshopRows = attachImportRowSkipping(convertConfig, workshopJob, {
            beforeFinish: async (rows, skip) => {
              const products = await mongo().collection<any>(Workshop._collectionName)
                .find({ _id: { $in: rows.map(row => row.id).filter(Boolean) } }).toArray();
              const existing = new Map(products.map(product => [product._id, product]));
              const categories = await mongo().collection<any>(Category._collectionName)
                .find({ _class: Category._class, institutionID }).toArray();
              const byID = new Map(categories.map(category => [category._id, category]));
              const byName = new Map(categories.map(category => [category.name, category]));
              const newCategories: any[] = [];
              for (const row of rows) {
                const entity = row.result.entity as any;
                const old = existing.get(row.id);
                if (old && old._class !== Workshop._class) {
                  skip(row, 'ID belongs to a different product class');
                  continue;
                }
                if (old && old.institutionID !== institutionID) {
                  skip(row, 'ID belongs to a different institution');
                  continue;
                }
                entity._id ||= A.getGloballyUniqueID();
                entity.institutionID = institutionID;
                const descriptions = entity[`translatedDescriptionHTML_${Language.ENGLISH}`]
                  ?? entity.translatedDescriptionHTML ?? [];
                entity.translatedDescriptionHTML = [
                  ...descriptions,
                  ...(old?.translatedDescriptionHTML ?? []).filter(description =>
                    ![Language.GERMAN, Language.ENGLISH].includes(description.langKey),
                  ),
                ];
                const prices = entity.prices.map(price => ({
                  ...old?.prices?.find(previous => previous._id === price._id),
                  ...price,
                }));
                // The CSV represents only the first price and German/English descriptions.
                entity.prices = [
                  ...prices,
                  ...(old?.prices?.slice(1) ?? []).filter(price => !prices.some(imported => imported._id === price._id)),
                ];
                entity.appointments = entity.appointments.map(appointment => ({
                  ...old?.appointments?.find(previous => previous._id === appointment._id),
                  ...appointment,
                }));
                let category = byID.get(entity.categoryID) ?? byName.get(entity.categoryName);
                if (!category && entity.categoryName?.trim()) {
                  category = {
                    _id: A.getGloballyUniqueID(), _class: Category._class, institutionID, name: entity.categoryName,
                  };
                  newCategories.push(category);
                  byID.set(category._id, category);
                  byName.set(category.name, category);
                }
                entity.categoryID = category?._id ?? null;
              }
              if (newCategories.length) await mongo().collection<any>(Category._collectionName).insertMany(newCategories);
            },
          });
          try {
            await runJob(convertConfig, workshopJob);
          } finally {
            summary.skipped.push(...workshopRows.skipped);
            summary.imported = workshopRows.writtenIDs.size;
          }

          const settingConfig = new ConvertConfig<Setting>({
            uniqueIdentifierFieldName: '_id',
            className: Setting._class,
            delimiter: ';',
            fieldMappings: [
              new FieldMappingEntry({ input: 'Einstellungen ID', output: '_id' }, v => v?.trim() ?? ''),
              new FieldMappingEntry({ input: 'ID', output: 'productID' }, v => v?.trim() ?? ''),
              { input: 'Kurs Ort', output: 'courseLocation' },
              new MaybeNumberFieldMappingEntry({ input: '1. E-Mail Erinnerung', output: 'firstReminderLeadTime' }),
              new MaybeNumberFieldMappingEntry({ input: '2. E-Mail Erinnerung', output: 'secondReminderLeadTime' }),
              new MaybeNumberFieldMappingEntry({ input: 'Abmeldezeitraum', output: 'blockSignOffDaysInAdvance' }),
              new MaybeNumberFieldMappingEntry({ input: 'Nicht Aufrücken Zeitraum', output: 'blockAutomaticMoveUpDaysInAdvance' }),
              new MaybeNumberFieldMappingEntry({ input: 'QR-Code Gültigkeit Dauer (Minuten)', output: 'qrCodeValidityInMinutes' }),
            ],
            foreignKeys: [],
          });
          const settingJob = new ImportJob<Setting>({ convertConfig: settingConfig });
          const skippedLines = new Set(workshopRows.skipped.map(row => row.line));
          const settingRows = attachImportRowSkipping(settingConfig, settingJob, {
            skipIDs: new Set(workshopRows.skipped.map(row => row.id).filter(id => !!id && !workshopRows.writtenIDs.has(id))),
            skipLines: skippedLines,
            rowID: result => String(result.entity.productID ?? ''),
            beforeFinish: async (rows, skip) => {
              const settings = await mongo().collection<any>(Setting._collectionName).find({ $or: [
                { _id: { $in: rows.map(row => row.result.entity._id).filter(Boolean) } },
                { productID: { $in: [...workshopRows.writtenIDs] } },
              ] }).toArray();
              const byID = new Map(settings.map(setting => [setting._id, setting]));
              const byProduct = new Map(settings.map(setting => [setting.productID, setting]));
              const seen = new Set<string>();
              for (const row of rows) {
                const entity = row.result.entity;
                const productID = workshopRows.writtenRows.get(row.line);
                if (!productID) {
                  skip(row, 'Workshop row was not written');
                  continue;
                }
                const old = entity._id ? byID.get(entity._id) : byProduct.get(productID);
                const ownedUnused = old && !old.productID && old.parentInstitutionID === institutionID;
                if (old && (old._class !== Setting._class || (old.productID !== productID && !ownedUnused) || old.institutionID)) {
                  skip(row, 'Settings ID belongs to a different product or scope');
                  continue;
                }
                entity._id ||= old?._id ?? A.getGloballyUniqueID();
                if (seen.has(entity._id!)) {
                  skip(row, 'Duplicate settings ID in file');
                  continue;
                }
                seen.add(entity._id!);
                entity.productID = productID;
                entity.parentInstitutionID = institutionID;
              }
            },
          });
          try {
            await runJob(settingConfig, settingJob);
          } finally {
            summary.skipped.push(...settingRows.skipped.filter(row => !skippedLines.has(row.line)));
          }
        } catch (error) {
          summary.error = error instanceof Error ? error.message : String(error);
        } finally {
          if (authorizedFile) {
            try {
              try {
                await mongo().collection<ImportFileDocument>(AdornisFile._collectionName)
                  .deleteOne({ _id: authorizedFile._id, _class: WorkshopImportFile._class });
              } finally {
                await unlinkImportFile(authorizedFile);
              }
            } catch (error) {
              summary.error ??= error instanceof Error ? error.message : String(error);
            }
          }
        }
        summary.skipped.sort((left, right) => left.line - right.line);
        subscriber.next(importSummaryJSON(summary));
        subscriber.complete();
      })();
    });
  },
});

export const getAllByTimeframe = registerQuery({
  operationName: 'getAllByTimeframe',
  type: () => [Workshop],
  params: [{ name: 'timeframeID', type: () => String }],
  resolve: timeframeID => {
    return async (gqlFields: BaseQLSelectionSet<Workshop>) => {
      const collection = await scopedCollection<Workshop>(Workshop._class);
      const result = await collection.find<Workshop>({ timeframeID }).toArray();
      return result;
    };
  },
});

export const getAllWorkshopsForInstitution = registerQuery({
  operationName: 'getAllWorkshopsForInstitution',
  type: () => [Workshop],
  params: [{ name: 'institutionID', type: () => String }],
  resolve: institutionID => {
    return async (gqlFields: BaseQLSelectionSet<Workshop>) => {
      const collection = await scopedCollection<Workshop>(Workshop._class);
      const result = await collection.find<Workshop>({ institutionID }).toArray();
      return result;
    };
  },
});

type ExportMapping = {
  input?: string;
  output?: string;
  serialize?: (value: unknown, data: unknown, i: number) => string | Promise<string>;
};

async function filterOutEmptyColumns(mappings: object[], rows: unknown[]): Promise<object[]> {
  if (!rows.length) return mappings;
  // Plain `{ input, output }` literals only get upgraded to FieldMappingEntry inside
  // ConvertConfig's entity constructor, so here we may see either shape.
  const serialize = async (m: ExportMapping, value: unknown, row: unknown, i: number): Promise<string> => {
    if (m.serialize) return m.serialize(value, row, i);
    return value == null ? '' : String(value);
  };
  const keep = await Promise.all(
    (mappings as ExportMapping[]).map(async (mapping, index) => {
      for (const row of rows) {
        let out: string;
        try {
          if (!mapping.output) {
            out = await serialize(mapping, undefined, row, index);
          } else {
            let raw: unknown;
            try {
              raw = Change.resolvePath(row as any, mapping.output.split('.'));
            } catch {
              raw = nothingHere;
            }
            if (raw === nothingHere) continue;
            out = await serialize(mapping, raw, row, index);
          }
        } catch {
          // A brittle serializer (e.g. date-fns on null) should not drop the column.
          return true;
        }
        if (out !== null && out !== undefined && String(out).trim() !== '') return true;
      }
      return false;
    }),
  );
  return mappings.filter((_, i) => keep[i]);
}

export const exportWorkshopParticipantsCSV = registerQuery({
  operationName: 'exportWorkshopParticipantsCSV',
  type: () => String,
  params: [
    { name: 'workshopID', type: () => String },
    { name: 'onlyParticipated', type: () => Boolean },
    { name: 'stammdatenFilter', type: () => String },
  ],
  resolve: (workshopID, onlyParticipated, stammdatenFilter?: string) => {
    return async () => {
      const product = await loadRawEntityDataByID(Workshop, workshopID, {
        _id: 1,
        institutionID: 1,
        appointments: { _id: 1, name: 1 },
      });
      if (!product) return;
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        const canExport = currentUser.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_EXPORTS, product.institutionID);
        const canManageParticipation = currentUser.hasPermission(
          Permission.MANAGE_PARTICIPATION,
          product.institutionID,
        );
        if (!canExport && !canManageParticipation) throw new ExpectedError('not authorized');
      }

      const participationCollection = mongo().collection<Participation>(Participation._collectionName);
      const exportData = participationCollection.raw.aggregate<ParticipationExportCSV>([
        {
          $match: {
            productID: workshopID,
            ...(onlyParticipated ? { state: ParticipationState.participated } : {}),
          },
        },
        { $replaceRoot: { newRoot: { participation: '$$ROOT' } } },
        { $lookup: { from: 'adornisuser', localField: 'participation.userID', foreignField: '_id', as: 'user' } },
        { $unwind: '$user' },
        {
          $lookup: {
            from: 'user-stammdaten',
            let: { uid: '$user._id' },
            pipeline: [{ $match: { institutionID: product.institutionID, $expr: { $eq: ['$userID', '$$uid'] } } }],
            as: 'stammdaten',
          },
        },
        { $set: { stammdaten: { $arrayElemAt: ['$stammdaten', 0] } } },
        { $sort: { 'participation.createdAt': -1 } },
        {
          $group: {
            _id: '$user._id',
            participation: { $first: '$participation' },
            user: { $first: '$user' },
            stammdaten: { $first: '$stammdaten' },
          },
        },
        { $addFields: { _class: ParticipationExportCSV._class } },
        {
          $match: stammdatenFilter
            ? Object.entries(JSON.parse(stammdatenFilter)).reduce(
                (acc, [key, value]) => ({ ...acc, ['stammdaten.' + key]: value }),
                {},
              )
            : {},
        },
      ]);

      //get the fields for the export via plugins...
      const plugin = getDbPluginForScope('registration::stammdaten', product.institutionID);
      const stammdatenFields = plugin.getFields(product.institutionID);
      const lecturersPlugin = getDbPluginForScope('participation-overview', product.institutionID);
      const fieldsToExport = await lecturersPlugin.getCSVFieldsForExport(
        stammdatenFields,
        plugin.classType().prototype.getTranslationNamespace(),
      );

      const currentUser = await getCurrentUserServer();
      const callerIsLecturer = currentUser?.roles?.some(role => role.name === Role.LECTURER);
      const isAdmin = currentUser?.roles?.some(role => role.name === Role.ADMIN || role.name === Role.SUPER_ADMIN);
      const isLecturerOnly = callerIsLecturer && !isAdmin;

      if (!isLecturerOnly) {
        for (const [i, appointment] of Object.entries(product.appointments ?? [])) {
          const input = appointment.name || 'Termin ' + (i + 1);
          const fieldMapping = new FieldMappingEntry(
            { input },
            undefined,
            (v: any, data: ParticipationExportCSV, i: number) =>
              (data.participation as any as ParticipationWorkshop).appointmentParticipations?.find(
                it => it.appointmentID === appointment._id,
              )
                ? 'Ja'
                : 'Nein',
          );
          fieldsToExport.push(fieldMapping);
        }

        fieldsToExport.push(
          new FieldMappingEntry({ input: 'Geburtsdatum' }, undefined, (v: any, data: ParticipationExportCSV) =>
            data.user.dateOfBirth ? format(data.user.dateOfBirth, 'dd.MM.yyyy') : '',
          ),
        );
        if (await isWhatsappConfiguredForInstitution(product.institutionID)) {
          fieldsToExport.push(
            new FieldMappingEntry({ input: 'WhatsApp aktiviert' }, undefined, (v: any, data: ParticipationExportCSV) =>
              data.user?.whatsappNotification ? 'Ja' : 'Nein',
            ),
          );
        }
      }

      // change column order
      fieldsToExport.sort((a, b) => {
        const priority = { ID: 0, Vorname: 1, Nachname: 2, Geburtsdatum: 3 };

        const pa = priority[(a as any).input] ?? 999;
        const pab = priority[(b as any).input] ?? 999;
        return pa - pab;
      });

      const rows = await exportData.toArray();
      if (rows.length === 0) throw new ExpectedError('file is empty');
      const nonEmptyMappings = await filterOutEmptyColumns(fieldsToExport, rows);

      const filePath = await exportCSVFromEntities(
        new ConvertConfig<ParticipationExportCSV>({
          className: ParticipationExportCSV._class,
          delimiter: ';',
          fieldMappings: nonEmptyMappings,
        }),
        (async function* () {
          for (const row of rows) yield row;
        })(),
        `export-teilnehmer-${A.getGloballyUniqueID()}`,
      );

      try {
        const stats = await stat(filePath);
        if (stats.size === 0) throw new Error('file is empty');
      } catch (e) {
        throw new Error('file is empty');
      }

      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  },
});

export const exportWorkshopCSV = registerQuery({
  operationName: 'exportWorkshopCSV',
  type: () => String,
  params: [
    { name: 'institutionID', type: () => String },
    { name: 'timeframeID', type: () => String },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_EXPORTS, params.institutionID),
    ),
  ],
  resolve: (institutionID, timeframeID?: Maybe<string>) => {
    return async () => {
      const workshops = mongo()
        .collection<Workshop>(Workshop._collectionName)
        .raw.aggregate<Workshop & { lastReminderMailSent?: Date }>([
          { $match: { _class: Workshop._class } },
          ...filterWorkshopsByInstitutionAggregationPipeline(institutionID),
          ...(timeframeID ? [{ $match: { timeframeID } }] : []),
          { $lookup: lookupEntityById(Category, 'categoryID', 'category') },
          { $set: { category: { $arrayElemAt: ['$category', 0] } } },
          { $lookup: { from: Setting._collectionName, as: 'settings', localField: '_id', foreignField: 'productID' } },
          { $set: { settings: { $arrayElemAt: ['$settings', 0] } } },
        ]);

      // This legacy field is not declared on Workshop and is dropped by the exporter's entity conversion.
      const reminderDates = new Map<string, Date | undefined>();
      const exportWorkshops = async function* () {
        for await (const workshop of workshops) {
          reminderDates.set(workshop._id, workshop.lastReminderMailSent);
          yield workshop;
        }
      };
      const reminderMapping = new MaybeDateTimeFieldMappingEntry({});
      const filePath = await exportCSVFromEntities(
        new ConvertConfig<Workshop>({
          className: Workshop._class,
          delimiter: ';',
          fieldMappings: [
            // relations
            { input: 'Kategorie ID', output: 'category._id' },
            { input: 'Kategorie', output: 'category.name' },
            { input: 'ID', output: '_id' },
            { input: 'institutionID', output: 'institutionID' },
            { input: 'Semester ID', output: 'timeframeID' },
            { input: 'Semester', output: 'timeframe.name' },
            // data
            { input: 'Name', output: 'name' },
            { input: 'Referent', output: 'speaker' },
            { input: 'Max. Teilnehmer', output: 'capacity' },
            { input: 'sellerID', output: 'sellerID' },
            new DescriptionFieldMapping({
              input: 'descriptionGermanHTML',
              output: 'translatedDescriptionHTML',
              language: Language.GERMAN,
            }),
            new DescriptionFieldMapping({
              input: 'descriptionEnglishHTML',
              output: 'translatedDescriptionHTML',
              language: Language.ENGLISH,
            }),
            { input: 'sortingWeight', output: 'sortingWeight' },
            { input: 'hasPrice', output: 'hasPrice' },
            { input: 'certificateID', output: 'certificateID' },
            new FieldMappingEntry({ input: 'lastReminderMailSent' }, undefined, (_, workshop: Workshop) => {
              const date = reminderDates.get(workshop._id);
              reminderDates.delete(workshop._id);
              return reminderMapping.serialize(date);
            }),
            { input: 'infoLink', output: 'infoLink' },
            { input: 'hideCapacity', output: 'hideCapacity' },
            { input: 'reference', output: 'reference' },
            { input: 'terminCopyID', output: 'terminCopyID' },
            // appointments
            new AppointmentFieldMapping({ input: 'Termine', output: 'appointments' }),
            // price
            { input: 'Preis Class', output: 'prices.0._class' },
            { input: 'Preis ID', output: 'prices.0._id' },
            new MaybeStringFieldMappingEntry({ input: 'Stripe ID', output: 'prices.0.stripeID' }),
            { input: 'Währung', output: 'prices.0.currency' },
            new NumberFieldMappingEntry({ input: 'Betrag', output: 'prices.0.unitAmount' }),
            /* settings */
            { input: 'Einstellungen ID', output: 'settings._id' },
            { input: 'Kurs Ort', output: 'settings.courseLocation' },
            { input: '1. E-Mail Erinnerung', output: 'settings.firstReminderLeadTime' },
            { input: '2. E-Mail Erinnerung', output: 'settings.secondReminderLeadTime' },
            { input: 'Abmeldezeitraum', output: 'settings.blockSignOffDaysInAdvance' },
            { input: 'Nicht Aufrücken Zeitraum', output: 'settings.blockAutomaticMoveUpDaysInAdvance' },
            { input: 'QR-Code Gültigkeit Dauer (Minuten)', output: 'settings.qrCodeValidityInMinutes' },
          ],
        }),
        exportWorkshops(),
        `export-workshop-${A.getGloballyUniqueID()}`,
      );
      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  },
});

export const exportWorkshopIkhInternalPaymentDataCSV = registerQuery({
  operationName: 'exportWorkshopIkhInternalPaymentDataCSV',
  type: () => String,
  params: [
    { name: 'institutionID', type: () => String },
    { name: 'start', type: () => Date },
    { name: 'end', type: () => Date },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_EXPORTS, params.institutionID),
    ),
  ],
  resolve: (institutionID, start, end) => {
    return async () => {
      const paymentPlugin = getDbPluginForScope('payment', institutionID);
      const paymentPluginConfig = paymentPlugin.internalPaymentExportConfig;
      if (!paymentPluginConfig) {
        throw new Error(
          `payment plugin ${paymentPlugin.id} does not configure internalPaymentExportConfig — internal payments export is unavailable for this institution`,
        );
      }

      const data = mongo()
        .collection<BHPayment>(BHPayment._collectionName)
        .raw.aggregate<BHPayment>([
          { $match: { amount: { $ne: [0, null] }, createdAt: { $gte: start, $lt: end } } },
          { $unwind: '$products' },
          {
            $lookup: {
              from: UserStammdaten._collectionName,
              localField: 'userReference.userID',
              foreignField: 'userID',
              as: 'stammdaten',
            },
          },
          { $unwind: '$stammdaten' },
          { $lookup: { from: User._collectionName, localField: 'userReference.userID', foreignField: '_id', as: 'user' } },
          { $set: { user: { $arrayElemAt: ['$user', 0] } } },
          {
            $project: {
              _class: ParticipationPaymentDataExportCSV._class,
              name: {
                $trim: {
                  input: { $concat: [{ $ifNull: ['$user.firstName', ''] }, ' ', { $ifNull: ['$user.lastName', ''] }] },
                },
              },
              price: '$amount',
              product: '$products.product',
              invoiceReceiver: '$stammdaten.invoiceReceiver',
              costCenterReceiver: paymentPluginConfig.costCenterReceiver,
              costCenter: '$stammdaten.costCenter',
              appointments: '$products.product.appointments',
              kostenart: paymentPluginConfig.kostenart,
              createdAt: '$createdAt',
              internalComments: '',
            },
          },
          {
            $match: {
              invoiceReceiver: paymentPluginConfig.invoiceReceiverEnum,
              'product.institutionID': institutionID,
            },
          },
        ]);

      const filePath = await exportCSVFromEntities(
        new ConvertConfig({
          className: ParticipationPaymentDataExportCSV._class,
          delimiter: ';',
          fieldMappings: [
            new MaybeStringFieldMappingEntry({ input: 'Kostenstelle Empfänger', output: 'costCenterReceiver' }),
            new MaybeStringFieldMappingEntry({ input: 'Kostenart', output: 'kostenart' }),
            new MaybeStringFieldMappingEntry({ input: 'Kostenstelle Zahlender', output: 'costCenter' }),
            new FieldMappingEntry(
              { input: 'Buchungsdatum' },
              undefined,
              (v: any, data: ParticipationExportCSV, i: number) => 'sofort',
            ),
            // "<participant>, <workshop days>" as the accounting expects it
            new FieldMappingEntry({ input: 'Beleg' }, undefined, (v: any, data: ParticipationPaymentDataExportCSV) =>
              [data.name, ...(data.appointments ?? []).map(({ start }) => formatBerlinDate(start, 'dd.MM.yyyy'))]
                .filter(Boolean)
                .join(', '),
            ),
            new MaybeStringFieldMappingEntry({ input: 'Workshop', output: 'product.name' }),
            new PriceFieldMapping({ input: 'Betrag', output: 'price' }),
            new MaybeStringFieldMappingEntry({ input: 'Interne Bemerkungen', output: 'internalComments' }),
          ],
        }),
        data,
        `export-internal-payment-data-${A.getGloballyUniqueID()}`,
      );

      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  },
});

export const exportPaymentReceiverCountsPerMonthCSV = registerQuery({
  operationName: 'exportPaymentReceiverCountsPerMonthCSV',
  type: () => String,
  params: [
    { name: 'institutionID', type: () => String },
    { name: 'start', type: () => Date },
    { name: 'end', type: () => Date },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_EXPORTS, params.institutionID),
    ),
  ],
  resolve: (institutionID: string, start: Date, end: Date) => {
    return async () => {
      const user = await getCurrentUserServerOrFail();
      if (!user.hasPermission(Permission.VIEW_ADMIN_DASHBOARD, institutionID)) throw new Error('no permission');

      const paymentPlugin = getDbPluginForScope('payment', institutionID);
      const paymentPluginConfig = paymentPlugin.internalPaymentExportConfig;
      if (!paymentPluginConfig) {
        throw new Error(
          `payment plugin ${paymentPlugin.id} does not configure internalPaymentExportConfig — internal payments export is unavailable for this institution`,
        );
      }

      // Server context returns the raw collection, which supports aggregation.
      const paymentCollection = (await runInContext(serverContext.sessionID, () =>
        scopedCollection<BHPayment>(BHPayment._class),
      )) as unknown as Pick<Collection<BHPayment>, 'aggregate'>;
      const rows = paymentCollection.aggregate<BHPayment>([
        {
          $match: {
            'products.product.institutionID': institutionID,
            status: { $nin: ['canceled', 'refund', 'failed'] },
          },
        },
        // Keep bundle payments intact: their participationIDs already cover all products.
        {
          $lookup: {
            from: Participation._collectionName,
            localField: 'participationIDs',
            foreignField: '_id',
            as: 'participations',
          },
        },
        {
          $set: {
            effectiveDate: {
              $cond: [{ $eq: [{ $type: '$createdAt' }, 'date'] }, '$createdAt', { $min: '$participations.createdAt' }],
            },
          },
        },
        { $match: { effectiveDate: { $gte: start, $lt: end } } },
        {
          $lookup: {
            from: UserStammdaten._collectionName,
            let: { userID: '$userReference.userID' },
            pipeline: [
              { $match: { institutionID, $expr: { $eq: ['$userID', '$$userID'] } } },
              { $project: { invoiceReceiver: 1 } },
            ],
            as: 'stammdaten',
          },
        },
        {
          $group: {
            _id: {
              month: { $dateToString: { format: '%Y-%m', date: '$effectiveDate', timezone: 'Europe/Berlin' } },
              internal: {
                $cond: [
                  { $eq: [{ $type: '$orderNr.type' }, 'missing'] },
                  { $in: [paymentPluginConfig.invoiceReceiverEnum, '$stammdaten.invoiceReceiver'] },
                  { $eq: ['$orderNr.type', 'intern'] },
                ],
              },
            },
            count: { $sum: { $size: { $ifNull: ['$participationIDs', []] } } },
          },
        },
        {
          $group: {
            _id: '$_id.month',
            internal: { $sum: { $cond: ['$_id.internal', '$count', 0] } },
            external: { $sum: { $cond: ['$_id.internal', 0, '$count'] } },
          },
        },
        { $sort: { _id: 1 } },
        {
          $project: {
            _id: 0,
            month: '$_id',
            internal: 1,
            external: 1,
            total: { $add: ['$internal', '$external'] },
          },
        },
      ]);

      const filePath = await exportCSVFromEntities(
        new ConvertConfig({
          className: BHPayment._class,
          delimiter: ';',
          fieldMappings: [
            new MaybeStringFieldMappingEntry({ input: 'Monat', output: 'month' }),
            new NumberFieldMappingEntry({ input: 'Intern', output: 'internal' }),
            new NumberFieldMappingEntry({ input: 'Extern', output: 'external' }),
            new NumberFieldMappingEntry({ input: 'Gesamt', output: 'total' }),
          ],
        }),
        rows,
        `export-payment-receiver-counts-${A.getGloballyUniqueID()}`,
      );

      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  },
});

export const getAppointmentCount = registerQuery({
  operationName: 'getAppointmentCount',
  type: () => Int,
  params: [{ name: 'filter', type: () => BHFilter }],
  resolve: filter => {
    return async () => {
      const c = mongo().collection(Workshop._collectionName);
      const productIDs = (() => {
        if (filter?.productID) return [filter.productID];
        return filter?.aspects?.find(aspect => aspect.fieldName === 'product._id')?.value?.split(',');
      })();
      const categoryIDs = (() => {
        if (filter?.categoryID) return [filter.categoryID];
        return filter?.aspects?.find(aspect => aspect.fieldName === 'product.category._id')?.value?.split(',');
      })();

      const aggregation = [
        {
          $match: {
            ...(productIDs ? { _id: { $in: productIDs } } : {}),
            ...(filter?.institutionID ? { institutionID: filter.institutionID } : {}),
            ...(filter?.timeframeID ? { timeframeID: filter?.timeframeID } : {}),
            ...(categoryIDs ? { categoryID: { $in: categoryIDs } } : {}),
          },
        },
        { $unwind: '$appointments' },
        { $group: { _id: null, count: { $sum: 1 } } },
      ];

      // logger.error({ filter, productIDs, categoryIDs, aggregation }, 'getAppointmentCount');

      const result = await c.aggregate(aggregation).toArray();

      return result[0]?.count ?? 0;
    };
  },
});

function filterWorkshopsByInstitutionAggregationPipeline(institutionID: string) {
  return [{ $match: { institutionID } }];
}

const subscribeLecturerWorkshops = (filter: BHFilter) =>
  watchQuery(Teaching._collectionName, { lecturerID: filter.lecturerID }).pipe(
    switchMap(async teachings => {
      const productIDs = teachings.map(teaching => teaching.productID);
      const workshopCollection = mongo().collection(Workshop._collectionName);
      const aggregation = [
        { $match: { _id: { $in: productIDs } } }, //
        { $sort: withIdTiebreaker(filter.getMongoOptions().sort) },
      ];

      const workshops = await workshopCollection.aggregate(aggregation, { collation: { locale: 'de' } }).toArray();
      if (context.serverContext) return workshops;

      // the lecturer sees all own workshops; admins only those of their institutions, so they
      // don't learn where else the person teaches
      const user = await getCurrentUserServerOrFail();
      if (user._id === filter.lecturerID) return workshops;
      return workshops.filter(workshop =>
        user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_LECTURERS, workshop.institutionID),
      );
    }),
  );

const workshopIsFinished = (workshop: Workshop) => {
  if (!workshop.appointments || !workshop.appointments.length || !workshop.appointments.at(-1)?.end) return false; // cant be finish when there are no appointments
  const lastAppointment = workshop.appointments.at(-1)!;
  return lastAppointment.end! < new Date();
};

export const subscribeFinishedWorkshopsForLecturer = registerSubscription({
  operationName: 'subscribeFinishedWorkshopsForLecturer',
  type: () => [Workshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Workshop>) => {
      return subscribeLecturerWorkshops(filter).pipe(
        map(workshops => workshops.filter(workshop => workshopIsFinished(workshop as any))),
        map(workshops => workshops.slice(skip, skip + limit) as Workshop[]),
      );
    };
  },
});

export const subscribeCurrentWorkshopsForLecturer = registerSubscription({
  operationName: 'subscribeCurrentWorkshopsForLecturer',
  type: () => [Workshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Workshop>) => {
      return subscribeLecturerWorkshops(filter).pipe(
        map(workshops => workshops.filter(workshop => !workshopIsFinished(workshop as any))),
        map(workshops => workshops.slice(skip, skip + limit) as Workshop[]),
      );
    };
  },
});

export const sendLecturersWorkshopReminderMail = registerMutation({
  operationName: 'sendLecturersWorkshopReminderMail',
  type: () => Int,
  params: [{ name: 'workshopID', type: () => String }],
  resolve: (workshopID: string) => {
    return async () => {
      const user = await getCurrentUserServerOrFail();
      const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, { institutionID: 1 });
      if (!user.hasPermission(Permission.VIEW_ADMIN_DASHBOARD, workshop.institutionID))
        throw new Error('no permission');

      const teachings = await loadRawEntityDataByQuery(Teaching, { productID: workshopID }, { _id: 1 });
      if (teachings.length === 0) {
        throw new ExpectedError('Diese Veranstaltung hat keine Dozenten. Es wurden keine E-Mails versendet.');
      }

      // must match LecturerWorkshopReminderMailStatus._class (entrypoint auto-mails)
      const mailClass = 'LecturerWorkshopReminderMailStatus';
      const autoMailLogsCollection = mongo().collection('auto-mail-logs');
      const failures: string[] = [];
      for (const teaching of teachings) {
        const mailID = `${mailClass}::ws_${workshopID}::tc_${teaching._id}`;
        let status: 'delivered' | 'failed' = 'delivered';
        let message: string | undefined;
        try {
          await notifyLecturerWorkshopReminder({ teachingID: teaching._id, workshopID });
        } catch (error) {
          status = 'failed';
          message = (error as any)?.message;
          failures.push(message ?? 'Unknown error');
        } finally {
          await autoMailLogsCollection.insertOne({
            _class: 'AutoMailLog',
            mailClass,
            mailID,
            status,
            message,
            timestamp: new Date(),
          });
        }
      }

      if (failures.length === teachings.length) {
        throw new Error(`Alle E-Mails fehlgeschlagen: ${failures[0]}`);
      }
      if (failures.length > 0) {
        throw new Error(`${failures.length} von ${teachings.length} E-Mails fehlgeschlagen.`);
      }

      return 1;
    };
  },
});

export const sendLecturersEvaluationMail = registerMutation({
  operationName: 'sendLecturersEvaluationMail',
  type: () => Int,
  params: [{ name: 'workshopID', type: () => String }],
  resolve: (workshopID: string) => {
    return async () => {
      const user = await getCurrentUserServerOrFail();
      const workshop = await loadRawEntityDataByIDOrFail(Workshop, workshopID, { institutionID: 1 });
      if (!user.hasPermission(Permission.VIEW_ADMIN_DASHBOARD, workshop.institutionID))
        throw new Error('no permission');

      const teachings = await loadRawEntityDataByQuery(Teaching, { productID: workshopID }, { _id: 1 });
      if (teachings.length === 0) {
        throw new ExpectedError('Diese Veranstaltung hat keine Dozenten. Es wurden keine E-Mails versendet.');
      }

      const mailClass = 'LecturerEvaluationMailStatus';
      const autoMailLogsCollection = mongo().collection('auto-mail-logs');
      const failures: string[] = [];
      for (const teaching of teachings) {
        const mailID = `${mailClass}::ws_${workshopID}::tc_${teaching._id}`;
        let status: 'delivered' | 'failed' = 'delivered';
        let message: string | undefined;
        try {
          await notifyLecturerEvaluationMail({ teachingID: teaching._id, workshopID });
        } catch (error) {
          status = 'failed';
          message = (error as any)?.message;
          failures.push(message ?? 'Unknown error');
        } finally {
          await autoMailLogsCollection.insertOne({
            _class: 'AutoMailLog',
            mailClass,
            mailID,
            status,
            message,
            timestamp: new Date(),
          });
        }
      }

      if (failures.length === teachings.length) {
        throw new Error(`Alle E-Mails fehlgeschlagen: ${failures[0]}`);
      }
      if (failures.length > 0) {
        throw new Error(`${failures.length} von ${teachings.length} E-Mails fehlgeschlagen.`);
      }

      return 1;
    };
  },
});

export const getWorkshopsByIDs = registerQuery({
  operationName: 'getWorkshopsByIDs',
  type: () => [Workshop],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Workshop>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Workshop, ids, gqlFields),
});

export const subscribeWorkshopsByIDs = registerSubscription({
  operationName: 'subscribeWorkshopsByIDs',
  type: () => [Workshop],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Workshop>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Workshop, ids, gqlFields),
});

export const getAllWorkshops = registerQuery({
  operationName: 'getAllWorkshops',
  type: () => [Workshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Workshop>>(gqlFields: SelSet) =>
      readAllEntities(Workshop, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllWorkshops = registerSubscription({
  operationName: 'subscribeAllWorkshops',
  type: () => [Workshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Workshop>>(gqlFields: SelSet) =>
      watchAllEntities(Workshop, gqlFields, { skip, limit, excludeIDs }),
});

export const getWorkshopByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Workshop>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getWorkshopsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeWorkshopByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Workshop>>(gqlFields: SelSet) =>
    id ? subscribeWorkshopsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
